import { GridCellParams, GridColDef } from '@mui/x-data-grid-pro'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import Button from 'ext-ui/components/Button/Button'
import moment, { Moment } from 'moment-timezone'
import React, { useMemo, useState } from 'react'
import { chevronColumnDef } from 'src/components/DataGrid/cells/ChevronCell'
import DataGridCell from 'src/components/DataGrid/cells/DataGridCell/DataGridCell'
import DataGridCellWrapper from 'src/components/DataGrid/cells/DataGridCellWrapper/DataGridCellWrapper'
import PillCell from 'src/components/DataGrid/cells/PillCell/PillCell'
import DataGrid, {
  DataGridHeaderStyle,
  DataGridRowHeight,
} from 'src/components/DataGrid/DataGrid'
import type { UtilityToolbarDropdownChildProps } from 'src/components/DataGrid/UtilityToolbar/UtilityToolbarDropdown'
import UtilityToolbarDropdown from 'src/components/DataGrid/UtilityToolbar/UtilityToolbarDropdown'
import UtilityToolbarSearch from 'src/components/DataGrid/UtilityToolbar/UtilityToolbarSearch'
import {
  DateRangeType,
  resolveDateRangeTypeWindow,
} from 'src/components/DateFilterDropdown/dateRangeType'
import DateRangePresetMenu from 'src/components/DateRangePresetMenu/DateRangePresetMenu'
import SvgCalendar from 'src/components/Svgs/SvgCalendar'
import SvgChevronRight from 'src/components/Svgs/SvgChevronRight'
import SvgPayInvoice from 'src/components/Svgs/SvgPayInvoice'
import {
  formatPaymentAmount,
  formatPaymentHistoryDate,
  paymentStatusPresentation,
} from 'src/feature/PaymentHistory/paymentHistoryPresentation'
import { useDebounceValue } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { openBillPaymentInvoice } from 'src/queries/payments/payments.download'
import { paymentsQueries } from 'src/queries/payments/payments.queries'
import {
  BillPaymentDisplayStatus,
  BillPaymentHistoryCriteria,
  BillPaymentHistoryRow,
} from 'src/queries/payments/payments.types'

import style from './PaymentHistoryTable.style'
import type { PaymentHistoryUrlState } from './paymentHistoryUrlState'

const PAGE_SIZE = 12
const TABLE_HEIGHT = 620
const EM_DASH = '—'

const STATUS_OPTIONS: BillPaymentDisplayStatus[] = [
  'ACTION_REQUIRED',
  'PROCESSING',
  'COMPLETED',
  'FAILED',
  'RETURNED',
  'CANCELLED',
]

type Translate = ReturnType<typeof useTranslate>

const toHalfOpenUtcRange = (
  since: Moment,
  until: Moment,
): Pick<BillPaymentHistoryCriteria, 'createdFrom' | 'createdBefore'> => ({
  createdFrom: since.clone().startOf('day').toISOString(),
  createdBefore: until.clone().add(1, 'day').startOf('day').toISOString(),
})

interface PaymentHistoryTableProps {
  organizationId: string
  viewerUserId: string
  urlState: PaymentHistoryUrlState
  onUrlStateChange: (state: PaymentHistoryUrlState) => void
  onOpenPayment: (paymentId: string) => void
  now?: Moment
  onAddBankAccount?: () => void
  onAddVendor?: () => void
}

interface PaymentHistoryDateMenuProps extends UtilityToolbarDropdownChildProps {
  dateRangeType: DateRangeType
  initialSince: Moment
  initialUntil: Moment
  since: Moment
  until: Moment
  onSelectLast30Days: () => void
  onSetSince: (value: Moment | undefined) => void
  onSetUntil: (value: Moment | undefined) => void
  translate: Translate
}

const PaymentHistoryDateMenu: React.FC<PaymentHistoryDateMenuProps> = ({
  open,
  close,
  dateRangeType,
  initialSince,
  initialUntil,
  since,
  until,
  onSelectLast30Days,
  onSetSince,
  onSetUntil,
  translate,
}) => (
  <DateRangePresetMenu
    options={[
      {
        key: DateRangeType.Last30Days,
        label: translate('Last 30 Days'),
        subLabel: `${initialSince.format('MMM D')} – ${initialUntil.format('MMM D, YYYY')}`,
        isActive: dateRangeType === DateRangeType.Last30Days,
      },
    ]}
    onSelectOption={() => {
      onSelectLast30Days()
      close?.()
    }}
    custom={{
      label: translate('Custom Date Range'),
      isActive: dateRangeType === DateRangeType.Custom,
      activeSubLabel: `${since.format('MMM D')} – ${until.format('MMM D, YYYY')}`,
      since,
      until,
      seedInactiveRange: true,
      setSince: onSetSince,
      setUntil: onSetUntil,
    }}
    backLabel={translate('Back')}
    parentOpen={open}
  />
)

interface PaymentHistoryStatusMenuProps
  extends UtilityToolbarDropdownChildProps {
  statuses: BillPaymentDisplayStatus[]
  onToggle: (status: BillPaymentDisplayStatus) => void
  translate: Translate
}

const PaymentHistoryStatusMenu: React.FC<PaymentHistoryStatusMenuProps> = ({
  statuses,
  onToggle,
  translate,
}) => (
  <div className="payment-history-status-menu">
    {STATUS_OPTIONS.map((status) => (
      <label key={status}>
        <input
          type="checkbox"
          data-testid={`payment-history-status-${status.toLowerCase()}`}
          checked={statuses.includes(status)}
          onChange={() => onToggle(status)}
        />
        {translate(paymentStatusPresentation(status).label)}
      </label>
    ))}
  </div>
)

const PaymentHistoryTable: React.FC<PaymentHistoryTableProps> = ({
  organizationId,
  viewerUserId,
  urlState,
  onUrlStateChange,
  onOpenPayment,
  now,
  onAddBankAccount,
  onAddVendor,
}) => {
  const translate = useTranslate()
  const queryClient = useQueryClient()
  const initialRange = useMemo(
    () =>
      resolveDateRangeTypeWindow({
        dateRangeType: DateRangeType.Last30Days,
        now,
      })!,
    [now],
  )
  const { dateRangeType, search, statuses, page } = urlState
  const since = moment(urlState.since, 'YYYY-MM-DD', true)
  const until = moment(urlState.until, 'YYYY-MM-DD', true)
  const [invoiceError, setInvoiceError] = useState(false)
  const debouncedSearch = useDebounceValue(search.trim(), 300)

  const updateUrlState = (
    patch: Partial<PaymentHistoryUrlState>,
    resetPage = true,
  ): void =>
    onUrlStateChange({
      ...urlState,
      ...patch,
      page: resetPage ? 0 : (patch.page ?? urlState.page),
    })

  const criteria = useMemo<BillPaymentHistoryCriteria>(
    () => ({
      page,
      count: PAGE_SIZE,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      statuses,
      ...toHalfOpenUtcRange(since, until),
    }),
    [debouncedSearch, page, since, statuses, until],
  )
  const historyQuery = useQuery(
    paymentsQueries.paymentHistory(organizationId, viewerUserId, criteria),
  )
  const payments = historyQuery.data?.payments ?? []
  const totalCount = historyQuery.data?.totalCount ?? 0
  const renderText =
    (value: (row: BillPaymentHistoryRow) => string) =>
    (params: GridCellParams<BillPaymentHistoryRow>): React.ReactElement => (
      <DataGridCell params={params} text={value(params.row)} />
    )

  const columns: GridColDef<BillPaymentHistoryRow>[] = [
    {
      field: 'vendor',
      headerName: translate('Vendor'),
      flex: 1.8,
      sortable: false,
      renderCell: (params) => (
        <DataGridCell
          params={params}
          text={params.row.vendor.name}
        />
      ),
    },
    {
      field: 'invoiceDueDate',
      headerName: translate('Due Date'),
      flex: 1.1,
      sortable: false,
      renderCell: renderText((row) =>
        formatPaymentHistoryDate(row.invoiceDueDate),
      ),
    },
    {
      field: 'amountCents',
      headerName: translate('Amount'),
      flex: 1,
      sortable: false,
      renderCell: renderText((row) =>
        formatPaymentAmount(row.amountCents, row.currency),
      ),
    },
    {
      field: 'invoiceNumber',
      headerName: translate('Invoice Number'),
      flex: 1.2,
      sortable: false,
      renderCell: renderText((row) => row.invoiceNumber),
    },
    {
      field: 'invoiceAttachment',
      headerName: translate('Invoices'),
      width: 90,
      sortable: false,
      renderCell: (params) => (
        <DataGridCellWrapper params={params}>
          {params.row.invoiceAttachment ? (
            <button
              type="button"
              className="payment-invoice-button"
              aria-label={translate('Open invoice ${number}', {
                number: params.row.invoiceNumber,
              })}
              onClick={(event) => {
                event.stopPropagation()
                setInvoiceError(false)
                void openBillPaymentInvoice(
                  queryClient,
                  organizationId,
                  params.row.invoiceAttachment!.id,
                ).catch(() => setInvoiceError(true))
              }}
            >
              <SvgPayInvoice aria-hidden="true" />
            </button>
          ) : (
            EM_DASH
          )}
        </DataGridCellWrapper>
      ),
    },
    {
      field: 'sender',
      headerName: translate('Payment Sent By'),
      flex: 1.4,
      sortable: false,
      renderCell: renderText(
        (row) => row.sender.displayName ?? row.sender.email ?? EM_DASH,
      ),
    },
    {
      field: 'displayStatus',
      headerName: translate('Status'),
      minWidth: 145,
      flex: 1.2,
      sortable: false,
      renderCell: (params) => {
        const presentation = paymentStatusPresentation(
          params.row.displayStatus,
        )
        return (
          <PillCell
            params={params}
            color={presentation.color}
            data-status={params.row.displayStatus}
          >
            {translate(presentation.label)}
          </PillCell>
        )
      },
    },
    {
      ...chevronColumnDef,
      renderCell: (params) => (
        <DataGridCellWrapper params={params}>
          <Button
            variant="no-style"
            aria-label={translate('View payment ${name}', {
              name: params.row.vendor.name,
            })}
            data-testid={`payment-history-open-${params.row.id}`}
            onClick={(event) => {
              event.stopPropagation()
              onOpenPayment(params.row.id)
            }}
          >
            <SvgChevronRight aria-hidden="true" />
          </Button>
        </DataGridCellWrapper>
      ),
    },
  ]

  const genuinelyEmpty =
    !historyQuery.isLoading &&
    !historyQuery.isError &&
    totalCount === 0 &&
    search.trim() === '' &&
    statuses.length === 0 &&
    dateRangeType === DateRangeType.Last30Days

  const filteredEmpty =
    !historyQuery.isLoading &&
    !historyQuery.isError &&
    totalCount === 0 &&
    !genuinelyEmpty

  const resetFilters = (): void => {
    onUrlStateChange({
      search: '',
      statuses: [],
      dateRangeType: DateRangeType.Last30Days,
      since: initialRange.since.format('YYYY-MM-DD'),
      until: initialRange.until.format('YYYY-MM-DD'),
      page: 0,
    })
  }

  const rangeStart = totalCount === 0 ? 0 : page * PAGE_SIZE + 1
  const rangeEnd = Math.min(totalCount, page * PAGE_SIZE + payments.length)
  const isLastPage = rangeEnd >= totalCount

  return (
    <div
      css={style}
      className="PaymentHistoryTable"
      data-testid="payment-history"
    >
      <div className="payment-history-toolbar">
        <UtilityToolbarSearch
          value={search}
          onChange={(value) => {
            updateUrlState({ search: value })
          }}
          placeholder={translate('Search payments')}
          noHeader
        />
        <UtilityToolbarDropdown
          buttonText={translate(
            dateRangeType === DateRangeType.Custom
              ? 'Custom Date Range'
              : 'Last 30 Days',
          )}
          image={<SvgCalendar />}
          dropdown={
            <PaymentHistoryDateMenu
              dateRangeType={dateRangeType}
              initialSince={initialRange.since}
              initialUntil={initialRange.until}
              since={since}
              until={until}
              onSelectLast30Days={() => {
                updateUrlState({
                  dateRangeType: DateRangeType.Last30Days,
                  since: initialRange.since.format('YYYY-MM-DD'),
                  until: initialRange.until.format('YYYY-MM-DD'),
                })
              }}
              onSetSince={(value) => {
                if (!value) return
                updateUrlState({
                  dateRangeType: DateRangeType.Custom,
                  since: value.format('YYYY-MM-DD'),
                })
              }}
              onSetUntil={(value) => {
                if (!value) return
                updateUrlState({
                  dateRangeType: DateRangeType.Custom,
                  until: value.format('YYYY-MM-DD'),
                })
              }}
              translate={translate}
            />
          }
          testId="payment-history-date-filter"
          alignment="right"
        />
        <UtilityToolbarDropdown
          buttonText={
            statuses.length === 0
              ? translate('All statuses')
              : translate('${count} statuses', { count: statuses.length })
          }
          selected={statuses.length > 0}
          testId="payment-history-status-filter"
          dropdown={
            <PaymentHistoryStatusMenu
              statuses={statuses}
              onToggle={(status) => {
                updateUrlState({
                  statuses: statuses.includes(status)
                    ? statuses.filter((item) => item !== status)
                    : [...statuses, status],
                })
              }}
              translate={translate}
            />
          }
          alignment="right"
        />
      </div>
      {invoiceError && (
        <div className="payment-history-invoice-error" role="alert">
          {translate('We could not download this invoice. Please try again.')}
        </div>
      )}
      {genuinelyEmpty ? (
        <div className="payment-history-empty">
          <div className="payment-history-empty-icon">
            <SvgPayInvoice aria-hidden="true" />
          </div>
          <h2>{translate('No payments yet')}</h2>
          <p>
            {translate(
              'Add a bank account and a vendor, then use Pay Bill in the top right to make your first payment.',
            )}
          </p>
          <p>{translate('Looking for an older payment? Try a custom date range.')}</p>
          <div className="payment-history-empty-actions">
            <Button variant="secondary" onClick={onAddBankAccount}>
              {translate('Add bank account')}
            </Button>
            <Button variant="secondary" onClick={onAddVendor}>
              {translate('Add vendor')}
            </Button>
          </div>
        </div>
      ) : historyQuery.isError ? (
        <div className="payment-history-message">
          <span>
            {translate('We could not load your payments. Please try again.')}
          </span>
          <Button
            variant="secondary"
            onClick={() => void historyQuery.refetch()}
          >
            {translate('Retry')}
          </Button>
        </div>
      ) : historyQuery.isLoading ? (
        <div className="payment-history-message">
          {translate('Loading payments…')}
        </div>
      ) : filteredEmpty ? (
        <div className="payment-history-message">
          <span>{translate('No payments match these filters.')}</span>
          <Button variant="secondary" onClick={resetFilters}>
            {translate('Reset filters')}
          </Button>
        </div>
      ) : (
        <>
          <div className="payment-history-grid">
            <DataGrid
              height={TABLE_HEIGHT}
              headerStyle={DataGridHeaderStyle.NoBackground}
              rowHeight={DataGridRowHeight.Default}
              rows={payments}
              columns={columns}
              filtersBody={{}}
              disableColumnFilter
              rowSelection={false}
              hideFooter
              onRowClick={(params) => onOpenPayment(params.row.id)}
            />
          </div>
          <div className="payment-history-footer">
            <span>
              {translate('Rows per page: ${count}', { count: PAGE_SIZE })}
            </span>
            <span>
              {translate('${start}-${end} of ${total}', {
                start: rangeStart,
                end: rangeEnd,
                total: totalCount,
              })}
            </span>
            <button
              type="button"
              aria-label={translate('Previous page')}
              disabled={page === 0 || historyQuery.isPlaceholderData}
              onClick={() =>
                updateUrlState({ page: Math.max(0, page - 1) }, false)
              }
            >
              {'‹'}
            </button>
            <button
              type="button"
              aria-label={translate('Next page')}
              disabled={isLastPage || historyQuery.isPlaceholderData}
              onClick={() => updateUrlState({ page: page + 1 }, false)}
            >
              {'›'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export default PaymentHistoryTable
